import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * Header Drawer with Accordion Submenus (NO conflict version)
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  connectedCallback() {
    super.connectedCallback();

    this.#injectAccordionStyles();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();

    // Back button logic
    this.addEventListener('click', (event) => {
      const backButton = event.target.closest('.menu-drawer__back-button');
      if (backButton) {
        event.preventDefault();
        const parentDetails = backButton.closest('details');
        if (parentDetails) this.#close(parentDetails);
      }
    });

    // 🔥 Custom accordion toggle fix (NO maxHeight)
    this.#initCustomAccordion();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Inject clean accordion CSS
   */
  #injectAccordionStyles() {
    if (document.querySelector('#header-drawer-accordion-styles')) return;

    const style = document.createElement('style');
    style.id = 'header-drawer-accordion-styles';

    style.textContent = `
      header-drawer .menu-drawer__submenu {
        position: relative !important;
        transform: none !important;
        width: 100% !important;
        visibility: visible !important;
        background: transparent !important;
        box-shadow: none !important;
        overflow: hidden;
        transition: max-height 0.3s ease;
      }

      header-drawer details:not([open]) .menu-drawer__submenu {
        max-height: 0;
      }

      header-drawer details[open] .menu-drawer__submenu {
        max-height: 500px;
      }

      header-drawer .menu-drawer__inner-submenu {
        position: relative !important;
        transform: none !important;
      }

      header-drawer .menu-drawer__back-button {
        display: none;
      }
    `;

    document.head.appendChild(style);
  }

  /**
   * ESC key close
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;
    this.#close(this.refs.details);
  };

  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;
    return event.target.closest('details') ?? this.refs.details;
  }

  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');
    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    requestAnimationFrame(() => {
      details.setAttribute('open', '');
      details.classList.add('menu-open');
      trapFocus(this.refs.details);
    });
  }

  close() {
    this.#close(this.refs.details);
  }

  #close(details) {
    const summary = details.querySelector('summary');
    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.removeAttribute('open');
    details.classList.remove('menu-open');

    if (details === this.refs.details) {
      onAnimationEnd(
        this.refs.menuDrawer,
        () => {
          removeTrapFocus();

          this.querySelectorAll('details[open]').forEach((el) => {
            el.removeAttribute('open');
            el.classList.remove('menu-open');
            el.querySelector('summary')?.setAttribute('aria-expanded', 'false');
          });
        },
        { subtree: false }
      );
    } else {
      trapFocus(this.refs.details);
    }
  }

  /**
   * 🔥 FIXED ACCORDION (NO CONFLICT)
   */
  #initCustomAccordion() {
    const accordions = this.querySelectorAll('.New_accordian_mobile_toggle');

    accordions.forEach((ul, index) => {
      const details = ul.closest('details');
      if (!details) return;

      const summary = details.querySelector('summary');
      if (!summary) return;

      // Create button
      const button = document.createElement('button');
      button.type = 'button';
      button.classList.add('accordion-toggle-btn');

      button.innerHTML = `
        <span>Menu ${index + 1}</span>
        <span class="icon">+</span>
      `;

      summary.appendChild(button);

      const icon = button.querySelector('.icon');

      button.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        const isOpen = details.hasAttribute('open');

        // Close all others
        this.querySelectorAll('details[open]').forEach((d) => {
          d.removeAttribute('open');
          d.querySelector('summary')?.setAttribute('aria-expanded', 'false');
          d.querySelector('.icon') && (d.querySelector('.icon').textContent = '+');
        });

        if (!isOpen) {
          details.setAttribute('open', '');
          summary.setAttribute('aria-expanded', 'true');
          icon.textContent = '-';
        } else {
          details.removeAttribute('open');
          summary.setAttribute('aria-expanded', 'false');
          icon.textContent = '+';
        }
      });
    });
  }

  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');

    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }
}

// Replace existing definition
if (customElements.get('header-drawer')) {
  console.warn('Replacing existing header-drawer with clean accordion version');
}

customElements.define('header-drawer', HeaderDrawer);